/* ============================================================================
   SL PRODUCTION ARTWORK  —  the file the operator actually makes the order from
   ============================================================================

   Turns a canonical proof plan into a production SVG: true physical size in
   inches, the customer's raster embedded, the ring and face as editable
   vectors, and — for shaped stickers only — a named `CutContour` path.

   ── WHY A NEW FILE AND NOT A BIGGER PREVIEW ──────────────────────────────
   `_artwork_preview` is a flattened PNG. It proves what the customer approved
   and it is useless for production: no vectors to nudge, no cut line, no
   physical size, and its pixels are the preview's, not the print's. The
   operator was therefore rebuilding each order by hand from `_artwork_original`
   plus a config file, re-deriving geometry the builder already knew exactly.

   ── THE ONE RULE THIS MODULE OBEYS ───────────────────────────────────────
   IT DERIVES NO GEOMETRY. Every number here comes from
   `SLBuilderCore.proofDrawPlan()`, the same plan the canvas proof executes.
   Scale, offset, rotation, flip, fit, ring, face and canvas are read, never
   recomputed. That is not tidiness: a production exporter that re-derived the
   layout would be a SECOND opinion about the customer's design, and the whole
   reason `_artwork_preview` on order #1014 came back byte-identical to the
   upload was two renderers disagreeing about one sticker.

   The only conversion performed is a UNIFORM px -> inch scale, expressed as
   `width`/`height` in inches over a `viewBox` in the plan's own pixel space.
   Uniform scaling cannot change a layout; it only states its size.

   ── WHAT ILLUSTRATOR ACTUALLY GETS (verified by hand, 2026-08-13) ────────
   Opens at the stated inch size, the embedded raster survives, the vectors
   stay separately editable, and `id="CutContour"` survives import as a named
   object.

   IT IS NOT A SPOT COLOUR. `docs/04-production/scan-station.md` requires the
   cut line to be a spot named exactly `CutContour` at C0 M75 Y5 K0 — and SVG
   has no separation/spot concept at all. What this file delivers is a path
   NAMED CutContour, stroked in the right pink at the right weight, that an
   operator or a JSX step can select and assign the real swatch to. Anything
   claiming more than that would be claiming the cutter will recognise it,
   which it will not.
   ============================================================================ */
(function (root) {
  'use strict';

  /* The cut line's appearance, from docs/04-production/scan-station.md: pink
     C0 M75 Y5 K0 at 1pt. The colour here is its sRGB stand-in — see the header
     on why this is a name and a hint, not a spot colour. */
  var CUT_ID = 'CutContour';
  var CUT_STROKE = '#E5007E';
  var CUT_WEIGHT_PT = 1;
  var PT_PER_INCH = 72;

  /* ---- pure: formatting ---------------------------------------------------- */

  /** Fixed-precision, trailing zeros stripped. Keeps 0.1+0.2 out of the file. */
  function n(v) {
    var x = +v;
    if (!isFinite(x)) return '0';
    var s = x.toFixed(4);
    if (s.indexOf('.') !== -1) s = s.replace(/\.?0+$/, '');
    return (s === '-0' || s === '') ? '0' : s;
  }

  /** XML text/attribute escaping. Customer filenames reach the <title>. */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  /* ---- pure: geometry EXPRESSION (not derivation) -------------------------- */

  /**
   * A rounded rectangle as an explicit path.
   *
   * `<rect rx>` would be shorter, but the cut line must arrive in Illustrator
   * as a PATH the operator can select, reshape and assign a spot colour to —
   * and a live rect is a shape object with its own rules. The ring and face use
   * the same helper so all three agree corner for corner.
   */
  function roundedRectPath(x, y, w, h, r) {
    if (!(w > 0) || !(h > 0)) return '';
    r = Math.max(0, Math.min(+r || 0, w / 2, h / 2));
    if (r <= 0) {
      return 'M' + n(x) + ' ' + n(y) + ' H' + n(x + w) + ' V' + n(y + h) + ' H' + n(x) + ' Z';
    }
    var a = 'A' + n(r) + ' ' + n(r) + ' 0 0 1 ';
    return 'M' + n(x + r) + ' ' + n(y) +
           ' H' + n(x + w - r) +
           ' ' + a + n(x + w) + ' ' + n(y + r) +
           ' V' + n(y + h - r) +
           ' ' + a + n(x + w - r) + ' ' + n(y + h) +
           ' H' + n(x + r) +
           ' ' + a + n(x) + ' ' + n(y + h - r) +
           ' V' + n(y + r) +
           ' ' + a + n(x + r) + ' ' + n(y) + ' Z';
  }

  /**
   * The art transform, straight out of `plan.art`.
   *
   * The order is the canvas rasterizer's and the CSS preview's — translate,
   * scale, rotate, flip — and SVG applies a transform list left to right, so
   * this string is the same sequence of operations rather than a translation
   * of it. All four are always emitted, including the identity flip, so the
   * file states the full transform instead of implying part of it.
   */
  function artTransform(art) {
    var deg = (+art.rotateRad || 0) * 180 / Math.PI;
    return 'translate(' + n(art.translate.x) + ' ' + n(art.translate.y) + ')' +
           ' scale(' + n(art.scale) + ')' +
           ' rotate(' + n(deg) + ')' +
           ' scale(' + (art.flipX ? '-1' : '1') + ' 1)';
  }

  /* ---- pure: the embed policy --------------------------------------------- */

  /* THIS FILE IS A BLUEPRINT, NOT THE QUALITY MASTER (D-091).
     ---------------------------------------------------------------------
     The previous policy sized the embed for PRINT: 300 DPI across the art's
     final physical size, 1.5x headroom, and anything under 4 MB carried
     verbatim whatever the arithmetic said. That followed from D-089, where
     this file was meant to be the one the operator prints from.

     It is not. `_artwork_original` is the quality master, it is mandatory, it
     is one click away in HQ, and it preserves the customer's own format and
     full resolution. This file's job is to say what the customer BUILT:
     ordered size, shape, crop, zoom, offset, rotation, flip, border,
     CutContour, Design ID. An operator reads those from it and prints from the
     original.

     The old policy never actually shrank anything, because both real orders
     fell under the 4 MB carry-verbatim rule. Order #1020 embedded a 1206x2622
     phone photo into a sticker HALF AN INCH across, and paid 2.5 MB — twice,
     before D-090 — for pixels no operator and no printer would ever see.

     So the embed is now sized for READING, on screen, in Illustrator:

       BLUEPRINT_DPI     200 across the ordered longest edge. Twice a typical
                         screen, so it stays clean at 200% zoom.
       BLUEPRINT_MIN_PX  a floor, because a 0.5in sticker at 200 DPI is 100px
                         and nobody can judge a crop from that.
       BLUEPRINT_MAX_PX  a cap. It is also <= the 1400px the BUILDER itself
                         displays, so the blueprint never claims detail the
                         customer never saw while designing.

     Never upsamples: a source already smaller than the target is carried as
     it is. The geometry around the raster is untouched by all of this — same
     artboard, same viewBox, same transform, same border, same CutContour. */
  var BLUEPRINT_DPI = 200;
  var BLUEPRINT_MIN_PX = 480;
  var BLUEPRINT_MAX_PX = 1024;

  /* FORMAT FOLLOWS TRANSPARENCY, AND NOTHING ELSE.
     ---------------------------------------------------------------------
     PNG is the worst possible container for a photograph: order #1020's
     221x480 blueprint is 101,066 bytes as PNG and 15,886 as JPEG q82 — the
     same picture, 6.4x the cost, for detail that is invisible at the size an
     operator reads it.

     But transparency is not decoration here. It IS the design: it decides
     where the sticker face shows through, what the crop looks like, and what
     a die-cut outline follows. JPEG has no alpha channel and would fill it —
     so art with alpha stays PNG, always, whatever it costs.

     Applies to the BLUEPRINT ONLY. The customer's original is never
     re-encoded, never resized, and never converted. */
  var BLUEPRINT_JPEG_QUALITY = 0.82;

  /**
   * The blueprint's long edge in pixels for a given ordered size.
   *
   * @param {number} longestIn  the ordered longest edge, in inches
   * @returns {number} pixels
   */
  function blueprintLongEdge(longestIn) {
    var L = +longestIn;
    if (!(L > 0)) return BLUEPRINT_MIN_PX;
    return Math.round(Math.min(BLUEPRINT_MAX_PX,
                               Math.max(BLUEPRINT_MIN_PX, L * BLUEPRINT_DPI)));
  }

  /**
   * Which container the blueprint raster should use.
   *
   * @param {boolean} hasAlpha  does the source carry transparency
   * @returns {Object} { mime, quality }  quality is ignored for PNG
   */
  function blueprintFormat(hasAlpha) {
    return hasAlpha
      ? { mime: 'image/png', quality: null }
      : { mime: 'image/jpeg', quality: BLUEPRINT_JPEG_QUALITY };
  }

  /**
   * Decide how the raster should be embedded. Pure — it computes the decision;
   * the browser half executes it.
   *
   * @param {Object} plan   the proof plan (art size + scale come from it)
   * @param {Object} o      { widthIn, heightIn, natW, natH, bytes }
   * @returns {Object|null} { action:'original'|'downsample', targetW, targetH,
   *                          artInchesW, artInchesH, longEdgePx, reason }
   */
  function planEmbed(plan, o) {
    o = o || {};
    if (!plan || !plan.canvas || !plan.art) return null;
    var natW = +o.natW, natH = +o.natH;
    var wIn = +o.widthIn, hIn = +o.heightIn;
    if (!(natW > 0) || !(natH > 0) || !(wIn > 0) || !(hIn > 0)) return null;

    /* THE DIAGNOSTICS ARE COMPUTED BEFORE ANY DECISION SHORT-CIRCUITS.
       ---------------------------------------------------------------------
       They used to be computed after the byte-threshold return, so the common
       case — a normal-sized upload, carried verbatim — reported
       `artInchesW: 0, artInchesH: 0`. The decision was right and the file was
       right; the numbers describing them were false, which is worse than
       having none, because a zero looks like a measurement. Whoever next read
       an embed log to answer "was there enough resolution for this print?"
       would have been told the art has no size.

       Diagnostics-only. The decision order below is unchanged. */
    var cw = +plan.canvas.w, ch = +plan.canvas.h;
    var scale = (+plan.art.scale > 0) ? +plan.art.scale : 1;
    var artInchesW = (cw > 0) ? Math.abs(+plan.art.w * scale * (wIn / cw)) : 0;
    var artInchesH = (ch > 0) ? Math.abs(+plan.art.h * scale * (hIn / ch)) : 0;
    if (!isFinite(artInchesW)) artInchesW = 0;
    if (!isFinite(artInchesH)) artInchesH = 0;

    /* Sized from the ORDERED size, not the art's own placed size. The operator
       is reading a document that is `wIn` x `hIn`; how far the customer zoomed
       inside it changes what the picture shows, not how many pixels are worth
       carrying to show it. */
    var longEdgePx = blueprintLongEdge(Math.max(wIn, hIn));

    /* Preserve the source aspect exactly: fit the natural size into the
       blueprint's long edge, and never scale UP. */
    var k = longEdgePx / Math.max(natW, natH);
    if (!(k > 0) || k >= 1) {
      return { action: 'original', targetW: natW, targetH: natH,
               artInchesW: artInchesW, artInchesH: artInchesH,
               longEdgePx: longEdgePx,
               reason: 'source is already at or below the blueprint size' };
    }
    return {
      action: 'downsample',
      longEdgePx: longEdgePx,
      targetW: Math.max(1, Math.round(natW * k)),
      targetH: Math.max(1, Math.round(natH * k)),
      artInchesW: artInchesW, artInchesH: artInchesH,
      reason: 'blueprint long edge ' + longEdgePx + 'px at ' +
              BLUEPRINT_DPI + ' DPI across the ordered size'
    };
  }

  /* ---- pure: the reference + metadata block -------------------------------- */

  /* WHAT SHOPIFY DOES TO A STORED RASTER — corrected, 2026-08-17.
     ---------------------------------------------------------------------
     An earlier note here said Shopify "handed back a LOSSY WebP", and used
     that to argue the production file should carry a better copy of the
     customer's artwork than `_artwork_original` does. Measured against the
     live store, the first half is delivery, not storage, and the second half
     no longer follows from it.

     Fetching the SAME `_artwork_original` URL three ways:

       a GENERIC Accept              image/png    71,399 B
       Chrome (accepts webp)        image/webp   35,080 B
       Safari (accepts webp)        image/webp   38,300 B

     Two different WebPs from one URL is content NEGOTIATION. The stored object
     is the PNG. `.json` and `.svg` are not negotiated at all.

     Shopify does still rewrite rasters on ingest, and not deterministically —
     71,351 uploaded became 71,399 on one order and 68,059 on another from the
     same source file, with eXIf/iCCP/cICP chunks injected. So the honest
     guarantee is FORMAT, DIMENSIONS and BIT DEPTH preserved, never byte
     identity. See D-091; the limitation is documented rather than worked
     around.

     None of which makes this file a quality master. The original is the
     quality master even re-encoded, because it is the customer's own image at
     full resolution; this file is the blueprint. */

  var REF_GAP_FRAC = 0.12;      // clear air between artboard and reference
  var REF_MIN_GAP = 12;

  /* `fitBox`, `bytesToDataUrl` and `mimeForOriginal` lived here to place the
     customer's original as an off-artboard reference copy. That copy is gone
     (see the Reference note below), and so are they — an exported helper that
     nothing calls is a suggestion to call it again. */

  /** One <text> line. Fonts are named generically — metadata is not artwork. */
  function textLine(x, y, size, fill, weight, s) {
    return '<text x="' + n(x) + '" y="' + n(y) + '" font-family="Helvetica, Arial, sans-serif"' +
           ' font-size="' + n(size) + '"' + (weight ? ' font-weight="' + weight + '"' : '') +
           ' fill="' + fill + '">' + esc(s) + '</text>';
  }

  /* ---- pure: the exporter -------------------------------------------------- */

  /**
   * Build the production SVG.
   *
   * @param {Object} plan  SLBuilderCore.proofDrawPlan() output — the authority
   * @param {Object} o     { widthIn, heightIn, href, designId, title, cutPath }
 *                        `borderBands` is an optional array of { d, fill },
 *                        outermost first, drawn under the artwork so a die-cut
 *                        border can be vector rather than dilated pixels. The
 *                        outermost band and `cutPath` are expected to be the
 *                        SAME path — see tests/production-artwork-wiring.
 *                        `cutPath` is an optional die-cut contour, already in
 *                        this document's viewBox coordinates. Supplied only by
 *                        a caller that reconstructed it; never derived here.
   * @returns {String|null} the SVG, or null if it cannot be built honestly
   */
  function buildProductionSvg(plan, o) {
    o = o || {};
    /* ANY missing input returns null. There is deliberately no partial file and
       no placeholder artwork: a production file that is missing the customer's
       design is more dangerous than no file, because it looks like one. */
    if (!plan || !plan.canvas || !plan.art) return null;
    var cw = +plan.canvas.w, ch = +plan.canvas.h;
    var wIn = +o.widthIn, hIn = +o.heightIn;
    if (!(cw > 0) || !(ch > 0) || !(wIn > 0) || !(hIn > 0)) return null;
    if (!(+plan.art.w > 0) || !(+plan.art.h > 0)) return null;

    /* The raster must be EMBEDDED. A remote URL would open in Illustrator as a
       broken link the day Shopify's CDN moves, which is the failure mode this
       whole durable-artwork effort exists to end. */
    var href = typeof o.href === 'string' ? o.href : '';
    if (href.slice(0, 5) !== 'data:') return null;

    /* Die-cut is READ FROM THE PLAN, not from a caller's opinion of it. The
       plan nulls ring and face for die-cut; that IS the definition. */
    var isDie = !plan.ring && !plan.face;

    /* ── ONE COORDINATE DENSITY, SO THE DOCUMENT MEANS ONE THING (10L) ────
       The root box and the viewBox used to be derived independently: the root
       from the ORDERED inches and the viewBox from the production frame. Those
       are two different roundings of one shape, so the document declared two
       different scales —

           600 / 3in   = 200 units/in        on x
           417 / 2.1in = 198.571 units/in    on y

       — and the file could not say how big a unit was without being asked
       which axis you meant.

       ── WHAT THAT ACTUALLY DID, WHICH IS WORSE THAN THE ARITHMETIC ───────
       The root carries no `preserveAspectRatio`, so it is `xMidYMid meet`: the
       renderer fits the viewBox UNIFORMLY into the root box at
       `min(wIn/cw, hIn/ch)` and letterboxes the remainder. Which axis binds
       depends on which way the ordered label happened to round.

       `heightIn` is `+(size / aspect).toFixed(1)` - a LABEL, rounded to one
       decimal - while the frame carries the unrounded finished aspect. When
       the label rounds UP the long axis binds and the sticker is right, with a
       thin letterbox nobody sees (07: content 2.085in inside a 2.1in canvas).
       When it rounds DOWN the SHORT axis binds and the whole document is
       scaled to fit it. Measured on 05-script-lettering: frame 600x488, label
       3 x 2.4in, true content height 2.44in, so the file rendered its ordered
       LONG EDGE at 2.9508in instead of 3.000in - a 1.64% shortfall on the one
       dimension the contour fit exists to pin exactly.

       So this is not only hygiene. The rounded label was silently resizing
       production files.

       ── THE FIX ──────────────────────────────────────────────────────────
       ONE density, taken from the ordered LONG edge, which is the dimension
       `slContourFit` pins exactly. The root box is then the frame expressed in
       inches at that density. Both axes divide by the same U by construction,
       the long edge is honoured exactly, `meet` has nothing left to letterbox,
       and NO COORDINATE MOVES: the geometry, the image rect and the cut path
       keep the numbers they already had. Only the document's statement of how
       big a unit is changes - from two answers to one.

       The short axis is now the TRUE finished height rather than the rounded
       label. That is the CutContour remaining the sizing truth, which is the
       contract: `heightIn` is what the customer is quoted, not what the cutter
       is handed. Ordered-size semantics are untouched - this function is not
       where they live.

       ── WHICH SIDE IS THE APPROXIMATION DEPENDS ON THE LANE ──────────────
       Only the die-cut frame is authoritative. 10K made `plan.canvas` an
       order-derived quantity for a CONTOUR order - `blueprintLongEdge` through
       the finished aspect, no DOM term in it - so there the frame is exact and
       the ordered label is the rounding, and the frame must win.

       A SHAPED sticker is the other way round. Its frame is still the preview
       box (10K left it there deliberately: `padPx` and the corner radii are
       carried in BOX PIXELS, so reframing without rescaling them would change
       the ring it prints), and its ORDERED size is exact - a 3 x 1.5in rounded
       rect really is 3 x 1.5in, with no fit involved. Deriving its root from
       the box would make the declared physical size follow the device, which
       `device-parity.test.js` catches and which would be worse than the
       anisotropy: measured, a 3x1.5in rounded rect declared 1.5043in on an
       iPhone SE and 1.5041in on an iPhone 15.

       So shaped keeps the ordered size it has always emitted. Its residual
       anisotropy is the preview box's own integer rounding, it is bounded by
       one pixel on each axis, and it is fixed properly by giving shaped a
       deterministic frame - the shaped half of 10K, not something to bodge
       here by moving its geometry. */
    var U, rootW, rootH;
    if (isDie) {
      U = Math.max(cw, ch) / Math.max(wIn, hIn);
      if (!(U > 0) || !isFinite(U)) return null;
      rootW = cw / U; rootH = ch / U;
    } else {
      rootW = wIn; rootH = hIn;
      U = cw / wIn;
    }

    var out = [];
    out.push('<?xml version="1.0" encoding="UTF-8"?>');
    out.push('<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"' +
             ' width="' + n(rootW) + 'in" height="' + n(rootH) + 'in"' +
             ' viewBox="0 0 ' + n(cw) + ' ' + n(ch) + '">');
    out.push('  <title>' + esc(o.title || o.designId || 'Stickr Lab production artwork') + '</title>');
    if (o.designId) out.push('  <desc>Design ' + esc(o.designId) + '</desc>');

    /* FLATTENED BY DEFAULT (D-105).
       ---------------------------------------------------------------------
       `o.flattened` means `href` is ALREADY the composited proof — ring, face
       and the clipped, transformed artwork, rendered by the canonical
       rasterizer from the same `proofDrawPlan`. It is drawn as ONE unclipped
       image, so the document contains no clipPath at all.

       WHY: Illustrator refused the old structure. Two NESTED `clip-path`
       groups wrapping a transformed <image> produced

           "The following items could not be imported properly:
            Clipping will be lost on roundtrip to Tiny"

       and, after continuing, the clip was not applied — so the artwork escaped
       its shape and the full-bleed white `StickerFace` and `StickerBorder`
       paths rendered as a giant white object over the sticker and its
       metadata. SVG Tiny cannot express clipping on arbitrary elements, and
       Illustrator warns whenever an imported document contains clipping it
       cannot round-trip.

       Nothing is lost that this file is for. It is a BLUEPRINT: the operator
       needs to SEE what the customer built, and the quality source is the
       separate original. Flattening the visual removes the only construct
       Illustrator objected to.

       CutContour is NOT flattened — it stays a real vector path below, because
       the cut is geometry, not a picture. A stroked path with no fill and no
       clip does not trigger the Tiny warning. */
    var clips = [];
    if (o.flattened) clips = null;
    if (clips && plan.ring) {
      clips.push('    <clipPath id="slRingClip"><path d="' +
        roundedRectPath(plan.ring.x, plan.ring.y, plan.ring.w, plan.ring.h,
                        resolveRadius(plan.ring.radius, Math.min(plan.ring.w, plan.ring.h))) +
        '"/></clipPath>');
    }
    if (clips && plan.face) {
      clips.push('    <clipPath id="slFaceClip"><path d="' +
        roundedRectPath(plan.face.x, plan.face.y, plan.face.w, plan.face.h,
                        resolveRadius(plan.face.radius, Math.min(plan.face.w, plan.face.h))) +
        '"/></clipPath>');
    }
    if (clips && clips.length) {
      out.push('  <defs>');
      for (var c = 0; c < clips.length; c++) out.push(clips[c]);
      out.push('  </defs>');
    }

    /* EVERYTHING THAT PRINTS LIVES IN ONE NAMED GROUP.
       ---------------------------------------------------------------------
       Probed in Illustrator 2026: an SVG `<g id>` imports as a NAMED GROUP,
       not as a layer. SVG has no layer concept and Illustrator does not invent
       one, so this is deliberately not described as a layer anywhere. What it
       gives us is what we actually need — a deterministic handle the JSX can
       resolve, so a reference image sitting beside the artboard can never be
       mistaken for cut geometry. */
    out.push('  <g id="Production">');

    /* The ring is the sticker's body; the face is what sits inside the border.
       A null fill means the plan said transparent, so nothing is painted —
       never a white rectangle standing in for transparency. */
    if (!o.flattened && plan.ring && plan.ring.fill) {
      out.push('  <path id="StickerBorder" d="' +
        roundedRectPath(plan.ring.x, plan.ring.y, plan.ring.w, plan.ring.h,
                        resolveRadius(plan.ring.radius, Math.min(plan.ring.w, plan.ring.h))) +
        '" fill="' + esc(plan.ring.fill) + '"/>');
    }
    if (!o.flattened && plan.face && plan.face.fill) {
      out.push('  <path id="StickerFace" d="' +
        roundedRectPath(plan.face.x, plan.face.y, plan.face.w, plan.face.h,
                        resolveRadius(plan.face.radius, Math.min(plan.face.w, plan.face.h))) +
        '" fill="' + esc(plan.face.fill) + '"/>');
    }

    /* The artwork, under the plan's clips and the plan's transform.
       `preserveAspectRatio="none"` because `plan.art.w/h` ALREADY encode the
       fit rule (contain for shaped, width for die-cut). Letting SVG fit the
       image again would be a second opinion about the layout. */
    if (o.flattened) {
      /* THE GENERATED BORDER, AS VECTOR, UNDERNEATH THE ARTWORK.
         ---------------------------------------------------------------
         A die-cut border used to be dilated pixels: the raster silhouette
         grown by a disk, so the border inherited every stair-step of the
         upload and the cut line inherited them again. `borderBands` lets the
         caller supply the SAME geometry it is about to use as the cut line —
         outermost first — so the printed edge and the cut edge are one path
         rather than two descriptions of one intention.

         This module still DERIVES nothing. It draws what it is handed, after
         checking the handed thing is a non-empty closed path, and when it is
         handed nothing the output is byte-identical to before.

         Shaped stickers never reach here: they own a deterministic ring, and
         their border is the plan's, not a reconstruction's. */
      if (isDie && o.borderBands && o.borderBands.length) {
        for (var bi = 0; bi < o.borderBands.length; bi++) {
          var band = o.borderBands[bi];
          if (!band || typeof band.d !== 'string' || !band.d.trim()) continue;
          if (!band.fill) continue;
          out.push('  <path class="slBorderBand" d="' + esc(band.d) + '"' +
                   ' fill="' + esc(band.fill) + '"/>');
        }
      }
      /* ONE image, filling the artboard, with no transform and no clip. The
         crop, zoom, offset, rotation, flip, shape, border and face treatment
         are already IN the pixels — the rasterizer applied the same plan. */
      out.push('  <image id="CustomerBuildPreview" xlink:href="' + esc(href) + '"' +
               ' x="0" y="0" width="' + n(cw) + '" height="' + n(ch) + '"/>');
    } else {
      var open = '', close = '';
      if (plan.ring && plan.ring.clip) { open += '<g clip-path="url(#slRingClip)">'; close = '</g>' + close; }
      if (plan.face && plan.face.clip) { open += '<g clip-path="url(#slFaceClip)">'; close = '</g>' + close; }
      if (open) out.push('  ' + open);
      out.push('    <g transform="' + artTransform(plan.art) + '">');
      /* The id sits on the IMAGE, not only on a wrapping group. Probed: an
         <image> with no id imports as an unnamed RasterItem even when its parent
         group is named, and an unnamed raster is exactly what a lookup cannot
         distinguish from the reference copy. */
      out.push('      <image id="CustomerArtwork" xlink:href="' + esc(href) + '"' +
               ' x="' + n(-plan.art.w / 2) + '" y="' + n(-plan.art.h / 2) + '"' +
               ' width="' + n(plan.art.w) + '" height="' + n(plan.art.h) + '"' +
               ' preserveAspectRatio="none"/>');
      out.push('    </g>');
      if (close) out.push('  ' + close);
    }

    /* THE CUT LINE — SHAPED STICKERS ONLY.
       ---------------------------------------------------------------------
       For a shaped sticker the cut IS the outer edge of the ring, which the
       plan states exactly, so the path is the real cut geometry.

       For DIE-CUT there is deliberately no CutContour. The builder never holds
       the contour as vector — `plan.art` points at a raster whose keyline is
       already burnt into its pixels — so the only path this module could emit
       is the bounding box, and a rectangle named CutContour is worse than an
       absent one: an operator trusting it would cut a square out of a die-cut
       sticker. Die-cut contouring stays with trace-command.jsx at the Scan
       Station, which traces the actual art.

       Stroke weight is 1pt expressed in viewBox units, so it stays 1pt however
       the file is scaled.

       10L: this reads the document's ONE density rather than re-deriving
       `cw / wIn`. The two agree whenever width is the long edge and disagree
       on a PORTRAIT order, where the long edge is the height - and a cut line
       that is 1pt on landscape and something else on portrait is the same
       class of defect as the frame this milestone is fixing. */
    var unitsPerInch = U;
    var cutWeight = n(unitsPerInch * CUT_WEIGHT_PT / PT_PER_INCH);
    if (!isDie && plan.ring) {
      out.push('  <path id="' + CUT_ID + '" d="' +
        roundedRectPath(plan.ring.x, plan.ring.y, plan.ring.w, plan.ring.h,
                        resolveRadius(plan.ring.radius, Math.min(plan.ring.w, plan.ring.h))) +
        '" fill="none" stroke="' + CUT_STROKE + '"' +
        ' stroke-width="' + cutWeight + '"/>');
    } else if (isDie && typeof o.cutPath === 'string' && o.cutPath.trim()) {
      /* `.trim()`: a whitespace-only string is TRUTHY, and would have emitted
         <path id="CutContour" d="   "> — an object Illustrator imports as an
         empty path that the JSX would then find and treat as the cut line. */
      /* DIE-CUT, WHEN A RECONSTRUCTED CONTOUR IS SUPPLIED (milestone 2).
         ---------------------------------------------------------------
         The paragraph above still holds in full: this module DERIVES no
         geometry, and it will not invent a die-cut contour. What changed is
         that a caller can now hand it one that was reconstructed from the
         customer's own silhouette by assets/sl-cutpath.js, already expressed
         in this document's coordinate system.

         The bounding-box objection that made die-cut carry no cut line is
         answered by evidence rather than by relaxing the rule: a supplied path
         is the measured outline of the approved sticker, not a rectangle
         around it. When no path is supplied — the engine refused, the mask
         could not be read, validation failed — this branch does not run and
         the file is byte-identical to what it has always been, which keeps
         trace-command.jsx the fallback it already is.

         Styling is deliberately IDENTICAL to the shaped lane above: the same
         name, the same pink, the same 1pt-in-viewBox-units weight. The RGB
         stroke is a name and a hint, never the cutter separation —
         cutcontour-command.jsx still owns the real spot. */
      out.push('  <path id="' + CUT_ID + '" d="' + esc(o.cutPath) + '"' +
        ' fill="none" stroke="' + CUT_STROKE + '"' +
        ' stroke-width="' + cutWeight + '"/>');
    }

    out.push('  </g>');          /* /Production — nothing below here prints */

    /* ── OFF THE ARTBOARD, AND IT MUST STAY THAT WAY ────────────────────
       The artboard is `width`/`height` in inches over the plan's own viewBox.
       Everything below is placed BEYOND `cw`, so it is carried by the file and
       ignored by the artboard. Verified in Illustrator: with a reference block
       present the artboard still measured exactly 3.0000 x 2.0000 in and the
       reference raster's bounds began past the artboard's right edge.

       Nothing here may touch `out`'s geometry above, and nothing here may
       widen the viewBox. If either happened the customer's ordered size would
       change, which is the one thing this file exists to state exactly. */
    var upi = cw / wIn;                                    // canvas units per inch
    var gap = Math.max(REF_MIN_GAP, Math.max(cw, ch) * REF_GAP_FRAC);
    var refX = cw + gap;
    var titleSize = upi * 0.13, lineSize = upi * 0.095, lineStep = upi * 0.135;
    var cursorY = titleSize;

    /* ── THE REFERENCE COPY IS GONE, ON PURPOSE ────────────────────────
       This file used to carry the customer's original a second time, as an
       off-artboard `OriginalArtwork` raster, so an operator could compare
       "what they sent" against "what they built" in one document.

       Real order #1020 showed what that costs. The production SVG was 6.77 MB
       and 100% of it was base64: `CustomerArtwork` at 3.23 MB and
       `OriginalArtwork` at 3.23 MB — the SAME image, 1206x2622, twice. With
       `_artwork_original` (2.42 MB) and a config that was itself another copy,
       one 2.42 MB photo was uploaded four times over on a single add to cart.

       The original is already durable, on the order, one click away. Carrying a
       second full copy inside the production file bought a side-by-side view
       and paid for it with megabytes on every checkout, on phones, on the
       slowest connections we serve. That trade is now refused.

       What survives is the part that was actually useful: the Metadata block
       still names the original file, the design, the size and the options — so
       the operator can still see WHAT the source was and go get it. The bytes
       just do not ride along.

       This supersedes the earlier one-file decision to embed the original.
       The production document remains fully SELF-CONTAINED for the thing it
       exists to print: `CustomerArtwork` is still embedded, never linked. */

    var meta = o.meta || null;
    if (meta) {
      var rows = [];
      if (meta.designId) rows.push('Design: ' + meta.designId);
      if (meta.sizeText) rows.push('Size: ' + meta.sizeText);
      if (meta.quantity != null) rows.push('Qty: ' + meta.quantity);
      if (meta.material) rows.push('Material: ' + meta.material);
      if (meta.finish) rows.push('Finish: ' + meta.finish);
      if (meta.shape) rows.push('Shape: ' + meta.shape);
      if (meta.border) rows.push('Border: ' + meta.border);
      if (meta.fileName) rows.push('Original: ' + meta.fileName);
      /* One short line, in the block that already exists. No schema change, no
         customer-visible property, and nothing an operator has to decode. */
      if (meta.cutPathNote) rows.push('Cut path: ' + meta.cutPathNote);
      /* Always present, so "no CutContour" can never again be a file that does
         not say why. */
      if (meta.cutDiag) rows.push('· cut: ' + meta.cutDiag);
      /* D-176: a refused cut is never silent - the customer was told a person
         draws it, and the file says so for HQ */
      if (meta.customerTold) rows.push('· customer told: ' + meta.customerTold);
      /* what the manufacturing rebuild did, so a cut line can be judged from
         the file rather than by eye */
      if (meta.cleanDiag) rows.push('· clean: ' + meta.cleanDiag);
      /* THE FIT CHAIN. Diagnostics, written beside the job so a file that
         comes out the wrong size can be explained from the file. Nothing in
         the pipeline reads these back and no geometry depends on them. */
      if (meta.fitDiag) {
        var fd = meta.fitDiag;
        rows.push('· fit: factor ' + fd.fitFactor + '  contourScale ' + fd.contourScale +
                  '  dieScale ' + fd.dieScale);
        rows.push('· border ' + fd.borderIn + 'in  in/px ' + fd.inchesPerPx +
                  '  finished ' + fd.finishedIn + 'in');
        rows.push('· artworkRectIn ' + fd.artworkRectIn);
        rows.push('· upload ' + fd.uploadPx + '  sil ' + fd.silPx +
                  '  frame ' + fd.framePx + ' pad ' + fd.framePad + ' rad ' + fd.frameRad +
                  ' basis ' + fd.radBasis);
        rows.push('· extent ' + fd.extent + '  box ' + fd.boxPx + '  painted ' + fd.paintedPx);
      }
      if (rows.length) {
        /* An operator summary, NOT the config. `_design_config` stays the
           machine record; a human should never have to open JSON to find out
           what they are making. */
        out.push('  <g id="Metadata">');
        out.push('    ' + textLine(refX, cursorY + titleSize, titleSize, '#111111', '700',
                                   'STICKR LAB — PRODUCTION REFERENCE'));
        var my = cursorY + titleSize + lineStep;
        for (var r = 0; r < rows.length; r++) {
          out.push('    ' + textLine(refX, my, lineSize, '#333333', null, rows[r]));
          my += lineStep;
        }
        out.push('  </g>');
      }
    }

    out.push('</svg>');
    return out.join('\n');
  }

  /* The radius rule belongs to the core (one rule, so the canvas proof and this
     file round the same corners). In the browser the core is always present; in
     Node the tests require it directly. The inline copy exists only so this
     module cannot throw if loaded alone, and is identical by construction. */
  function resolveRadius(val, box) {
    var core = (root && root.slBuilder) || null;
    if (core && core.resolveRadius) return core.resolveRadius(val, box);
    var b = +box > 0 ? +box : 0;
    if (val === '50%' || val === '999px') return b / 2;
    var v = parseFloat(val);
    return isFinite(v) ? Math.min(Math.max(v, 0), b / 2) : 0;
  }

  var API = {
    version: 'production-artwork-v1',
    CUT_ID: CUT_ID, CUT_STROKE: CUT_STROKE, CUT_WEIGHT_PT: CUT_WEIGHT_PT,
    BLUEPRINT_DPI: BLUEPRINT_DPI, BLUEPRINT_MIN_PX: BLUEPRINT_MIN_PX,
    BLUEPRINT_MAX_PX: BLUEPRINT_MAX_PX, BLUEPRINT_JPEG_QUALITY: BLUEPRINT_JPEG_QUALITY,
    blueprintLongEdge: blueprintLongEdge, blueprintFormat: blueprintFormat,
    // pure
    n: n, esc: esc, roundedRectPath: roundedRectPath, artTransform: artTransform,
    planEmbed: planEmbed, buildProductionSvg: buildProductionSvg
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLProductionArtwork = API;
})(typeof window !== 'undefined' ? window : null);
